import GoogleMapReact from 'google-map-react'
import MarkerClusterer from '@google/markerclustererplus'
import JobAdsMapFilters from 'portal_components/map/JobAdsMapFilters.js.jsx'
import createMarkerIconPath from 'portal_components/map/markerIcon.js'

const MARKER_ICON = createMarkerIconPath(30, 30)
const CLUSTER_ICON = createMarkerIconPath(50, 50)

const CLUSTER_STYLES = [
  {
    url: CLUSTER_ICON,
    width: 50,
    height: 50
  }
]

const GLOBE_WIDTH = 256
const GERMANY_CENTER = { lat: 51.1657, lng: 10.4515 }
const DEFAULT_ZOOM = 7
const MAX_ZOOM = 15

// Google calls this global when the API key is invalid/unauthorized for the current
// domain (e.g. restricted keys on local dev). The map object it hands back afterwards
// is only partially initialized, so building markers/clusters on it throws uncaught
// errors deep inside the clusterer's own render loop instead of failing where we can catch it.
let googleMapsAuthFailed = false
window.gm_authFailure = () => {
  googleMapsAuthFailed = true
}

// Other Google Maps failures (e.g. DeletedApiProjectMapError) skip gm_authFailure entirely
// and still hand back a map object that looks usable. The crash then happens later, inside
// Google's own overlay render loop calling into the clusterer's draw() method asynchronously -
// outside any call stack of ours, so it can't be caught with a try/catch around our own code.
// Suppress only this specific vendor-originated error so real app errors still surface normally.
window.addEventListener('error', (event) => {
  if (event.filename && event.filename.includes('markerclustererplus')) {
    event.preventDefault()
  }
})

export default class JobAdsMap extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      loaded: false,
      map: null,
      maps: null,
      mapUrlKeys: {
        key: gon.application.gmaps_key,
        language: I18n.locale,
        libraries: 'places'
      },
      mapOptions: {
        maxZoom: MAX_ZOOM
      },
      center: GERMANY_CENTER,
      zoom: DEFAULT_ZOOM,
      position: this.__positionFromParams(),
      distance: this.__distanceFromParams(),
      jobAdMarkers: {},
      jobAdInfoWindows: {},
      clusterer: null,
      searchCircle: null
    }
  }

  render () {
    return (
      <div className='location-search'>
        <div className='map-container'>
          <GoogleMapReact
            bootstrapURLKeys={this.state.mapUrlKeys}
            options={this.state.mapOptions}
            center={this.state.center}
            zoom={this.state.zoom}
            onGoogleApiLoaded={({ map, maps }) => this.__onApiLoaded(map, maps)}
            yesIWantToUseGoogleMapApiInternals
          />
        </div>

        <JobAdsMapFilters
          map={this.state.map}
          maps={this.state.maps}
          availableJobAdLocations={this.props.job_ads_locations}
          searchParams={this.props.search_params}
          defaultPosition={this.__positionFromParams()}
          defaultDistance={this.__distanceFromParams()}
          distanceSearchActive={this.__isDistanceSearchActive()}
        />
      </div>
    )
  }

  __onApiLoaded (map, maps) {
    window.jobAdsMap = map

    this.setState({
      loaded: true,
      map,
      maps,
    }, () => {
      if (googleMapsAuthFailed) return

      this.__createMarkerClusters()
      this.__createSearchCircle()
    })
  }

  __createMarkerClusters () {
    if (this.state.loaded) {
      let clusterer = this.state.clusterer

      if (clusterer == null) {
        clusterer = this.__createClusterer()
        this.setState({ clusterer })
      }

      clusterer.clearMarkers()

      for (const jobAd of this.props.job_ads) {
        for (const location of jobAd.locations) {
          const markerKey = jobAd['id'] + '/' + location['id'];
          const marker = this.state.jobAdMarkers[markerKey] || this.__createMarker(jobAd, location);

          this.state.jobAdMarkers[markerKey] = marker
          clusterer.addMarker(marker)
        }
      }

      clusterer.repaint()

      this.__fitMarkersBounds()
    }
  }

  __createClusterer () {
    const clusterer = new MarkerClusterer(this.state.map, [], {
      averageCenter: true,
      ignoreHidden: true,
      maxZoom: MAX_ZOOM - 1,
      styles: CLUSTER_STYLES
    });

    this.state.map.addListener('idle', () => {
      clusterer.repaint()
      this.state.maps.event.clearListeners(this.state.map, 'bounds_changed')
    })

    return clusterer
  }

  __createMarker (jobAd, address) {
    const { maps, map } = this.state

    // Dirty but reasonably good solution to ensure that markers does not overlap
    let newLat = address.latitude + (Math.random() / 500)
    let newLng = address.longitude + (Math.random() / 500)

    let marker = new this.state.maps.Marker({
      title: jobAd.name,
      position: { lat: newLat, lng: newLng },
      icon: MARKER_ICON
    })

    let infoWindow = new maps.InfoWindow({
      content: jobAd.info_html
    })

    marker.addListener('click', () => {
      infoWindow.open(map, marker)
    })

    return marker
  }

  __createSearchCircle () {
    if (this.__isDistanceSearchActive()) {
      const searchCircle = new this.state.maps.Circle({
        strokeColor: '#015c19',
        strokeOpacity: 0.25,
        strokeWeight: 1,
        fillColor: '#3dff71',
        fillOpacity: 0.15,
        map: this.state.map,
        center: this.state.position,
        radius: this.state.distance * 1000
      });

      this.setState({ searchCircle }, () => {
        this.__fitDistanceSearchBounds()
      })
    }
  }

  __positionFromParams () {
    if (_.isEmpty(this.props.search_params)) {
      return null
    }

    return {
      lat: parseFloat(this.props.search_params.location_latitude),
      lng: parseFloat(this.props.search_params.location_longitude)
    };
  }

  __distanceFromParams () {
    if (_.isEmpty(this.props.search_params)) {
      return null
    }

    return parseFloat(this.props.search_params.location_distance)
  }

  __isPositionDefined () {
    return this.state.position && _.isFinite(this.state.position.lat) && _.isFinite(this.state.position.lng)
  }

  __isDistanceDefined () {
    return this.state.distance && _.isFinite(this.state.distance)
  }

  __isDistanceSearchActive () {
    return this.__isPositionDefined() && this.__isDistanceDefined()
  }

  __fitMarkersBounds () {
    if (this.__isPositionDefined()) {
      this.setState({ center: this.state.position, zoom: DEFAULT_ZOOM })
      return;
    }

    const bounds = new this.state.maps.LatLngBounds();

    for (const jobAd of this.props.job_ads) {
      for (const address of jobAd.locations) {
        bounds.extend(new this.state.maps.LatLng(address.latitude, address.longitude))
      }
    }

    if (bounds.isEmpty()) {
      this.setState({
        center: GERMANY_CENTER,
        zoom: DEFAULT_ZOOM
      })
    } else {
      this.setState({
        center: { lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() },
        zoom: this.__getZoomFromBounds(bounds)
      })
    }
  }

  __fitDistanceSearchBounds () {
    this.setState({
      center: { lat: this.state.searchCircle.getCenter().lat(), lng: this.state.searchCircle.getCenter().lng() },
      zoom: this.__getZoomFromBounds(this.state.searchCircle.getBounds())
    })
  }

  __getZoomFromBounds (latLngBounds) {
    const pixelWidth = this.state.map.getDiv().offsetWidth;
    const west = latLngBounds.getSouthWest().lng();
    const east = latLngBounds.getNorthEast().lng();
    let angle = east - west

    if (angle < 0) {
      angle += 360
    }

    const zoom = Math.round(Math.log(pixelWidth * 360 / angle / GLOBE_WIDTH) / Math.LN2) - 2;
    return Math.min(zoom, MAX_ZOOM)
  }
}
